import studentApi from './studentApi';
import { API_ENDPOINTS } from './api';

export interface CourseEnrollment {
  id: number;
  userId: number;
  courseId: number;
  status: string;
  progressPercentage: number;
  enrolledAt: string;
  enrolledBy?: string;
  preferredViewId?: number | null;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  timestamp: string;
  errorCode?: string;
}

class CourseEnrollmentService {
  /**
   * Enroll a user (child) in a course
   */
  async enrollUserInCourse(
    userId: number, 
    courseId: number, 
    enrolledBy?: string, 
    classId?: number
  ): Promise<CourseEnrollment> {
    try {
      const params = new URLSearchParams({
        userId: userId.toString(),
        courseId: courseId.toString(),
      });
      
      if (enrolledBy) {
        params.append('enrolledBy', enrolledBy);
      }
      
      if (classId) {
        params.append('classId', classId.toString());
      }
      
      const response = await studentApi.post<ApiResponse<CourseEnrollment>>(
        `${API_ENDPOINTS.ENROLLMENTS.ENROLL}?${params.toString()}`
      );
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to enroll in course');
      }
    } catch (error: any) {
      console.error('Error enrolling in course:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to enroll in course'
      );
    }
  }

  /**
   * Persist the student's preferred course structure view for an enrollment.
   */
  async updatePreferredView(userId: number, courseId: number, viewId: number | null): Promise<CourseEnrollment> {
    const params = new URLSearchParams({
      userId: userId.toString(),
    });
    if (viewId != null) {
      params.append('viewId', viewId.toString());
    }
    const response = await studentApi.put<ApiResponse<CourseEnrollment>>(
      `${API_ENDPOINTS.COURSES.DETAILS}/${courseId}/enrollment/preferred-view?${params.toString()}`
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update preferred view');
  }

  /**
   * Get enrollments for a user
   */
  async getUserEnrollments(userId: number): Promise<CourseEnrollment[]> {
    try {
      const response = await studentApi.get<ApiResponse<CourseEnrollment[]>>(
        `${API_ENDPOINTS.COURSES.DETAILS}/user/${userId}/enrollments`
      );
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch enrollments');
      }
    } catch (error: any) {
      console.error('Error fetching enrollments:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch enrollments'
      );
    }
  }

  /**
   * Get active enrollments for a user
   */
  async getActiveEnrollments(userId: number): Promise<CourseEnrollment[]> {
    try {
      const response = await studentApi.get<ApiResponse<CourseEnrollment[]>>(
        `${API_ENDPOINTS.COURSES.DETAILS}/user/${userId}/enrollments/active`
      );
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch active enrollments');
      }
    } catch (error: any) {
      console.error('Error fetching active enrollments:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch active enrollments'
      );
    }
  }

  /**
   * Check if user is enrolled in a course
   */
  async isUserEnrolled(userId: number, courseId: number): Promise<boolean> {
    try {
      const response = await studentApi.get<ApiResponse<boolean>>(
        `${API_ENDPOINTS.COURSES.DETAILS}/${courseId}/user/${userId}/enrolled`
      );
      
      if (response.data.success && response.data.data !== undefined) {
        return response.data.data;
      } else {
        return false;
      }
    } catch (error: any) {
      console.error('Error checking enrollment:', error);
      return false;
    }
  }
}

export const courseEnrollmentService = new CourseEnrollmentService();
export default courseEnrollmentService;

